// Contratto dati unico BE↔FE: schemi Zod di RISPOSTA per le pagine
// operative del master (billing, interni, trunk, template tenant, quote DB).
// Route backend: `master/billing.routes.ts`, `master/extensions.routes.ts`,
// `master/trunks.routes.ts`, `master/templates.routes.ts`,
// `master/db-quota.routes.ts`, `master/embeddings.routes.ts`.
// Tipi derivati con `z.infer`, SoT unica condivisa. Vedere docs/zod-boundary.md.
import { z } from 'zod';
import { quotaStatusSchema } from './storage-quota';

// ─── Billing ────────────────────────────────────────────────────────────────

/** `GET /master/billing/overview` → `data`: una riga per tenant. */
export const masterBillingOverviewRowSchema = z.object({
  tenant_id: z.string(),
  tenant_name: z.string(),
  tenant_slug: z.string(),
  total_calls: z.number(),
  answered_calls: z.number(),
  inbound_calls: z.number(),
  outbound_calls: z.number(),
  total_duration_seconds: z.number(),
  outbound_duration_seconds: z.number(),
  total_duration_minutes: z.number(),
  outbound_duration_minutes: z.number(),
  active_extensions: z.number(),
  active_trunks: z.number(),
});
export type MasterBillingOverviewRow = z.infer<typeof masterBillingOverviewRowSchema>;

/** `GET /master/billing/stats` → `data`. */
export const masterBillingStatsSchema = z.object({
  total_calls: z.number(),
  answered_calls: z.number(),
  missed_calls: z.number(),
  busy_calls: z.number(),
  inbound_calls: z.number(),
  outbound_calls: z.number(),
  total_duration_seconds: z.number(),
  total_duration_minutes: z.number(),
  avg_duration_seconds: z.number(),
  active_tenants: z.number(),
  answer_rate: z.number(),
});
export type MasterBillingStats = z.infer<typeof masterBillingStatsSchema>;

/** `GET /master/embeddings/health` → `data`. */
export const masterEmbeddingHealthSchema = z.object({
  expected_dim: z.number(),
  policy: z.enum(['strict', 'pad-or-truncate']),
  current_model: z.string().nullable(),
  current_provider: z.string().nullable(),
  mismatch_counter_process: z.number(),
  last_24h: z.object({
    total: z.number(),
    by_action: z.record(z.string(), z.number()),
    by_model: z.array(
      z.object({ model: z.string(), provider: z.string(), count: z.number(), last_seen: z.string() }),
    ),
  }),
});
export type MasterEmbeddingHealth = z.infer<typeof masterEmbeddingHealthSchema>;

// ─── Interni (extensions) cross-tenant ──────────────────────────────────────

/** `GET /master/extensions` → `data[]`. */
export const masterExtensionRowSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  tenant_name: z.string(),
  tenant_slug: z.string(),
  tenant_status: z.string(),
  store_id: z.string().nullable(),
  store_name: z.string().nullable(),
  extension: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string().nullable(),
  status: z.string(),
  sip_device_type: z.string(),
  outbound_caller_id: z.string().nullable(),
  settings: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  active_extensions_count: z.number(),
  legal_name: z.string().optional(),
  vat_number: z.string().optional(),
});
export type MasterExtensionRow = z.infer<typeof masterExtensionRowSchema>;

/** `GET /master/extensions/stats` → `data`. */
export const masterExtensionStatsSchema = z.object({
  total_extensions: z.number(),
  active_extensions: z.number(),
  inactive_extensions: z.number(),
  tenants_with_extensions: z.number(),
  stores_with_extensions: z.number(),
  softphones: z.number(),
  deskphones: z.number(),
  extensions_with_email: z.number(),
});
export type MasterExtensionStats = z.infer<typeof masterExtensionStatsSchema>;

// ─── Trunk cross-tenant ─────────────────────────────────────────────────────

/** `GET /master/trunks` → `data[]`. */
export const masterTrunkRowSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  tenant_name: z.string(),
  tenant_slug: z.string(),
  tenant_status: z.string(),
  name: z.string(),
  host: z.string(),
  port: z.number(),
  username: z.string(),
  status: z.string(),
  trunk_type: z.string(),
  max_channels: z.number(),
  settings: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  active_trunks_count: z.number(),
  legal_name: z.string().optional(),
  vat_number: z.string().optional(),
});
export type MasterTrunkRow = z.infer<typeof masterTrunkRowSchema>;

/** `GET /master/trunks/stats` → `data`. */
export const masterTrunkStatsSchema = z.object({
  total_trunks: z.number(),
  active_trunks: z.number(),
  inactive_trunks: z.number(),
  tenants_with_trunks: z.number(),
  total_max_channels: z.number(),
  sip_trunks: z.number(),
  iax2_trunks: z.number(),
});
export type MasterTrunkStats = z.infer<typeof masterTrunkStatsSchema>;

// ─── Template tenant ────────────────────────────────────────────────────────

/** `GET /master/templates` → `data[]`. */
export const masterTenantTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  config: z.record(z.string(), z.unknown()),
  is_default: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type MasterTenantTemplate = z.infer<typeof masterTenantTemplateSchema>;

// ─── Quote DB per tenant ────────────────────────────────────────────────────

/** `GET /master/storage/db/tenants` → `data[]` (riga lista). */
export const masterTenantDbQuotaRowSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string(),
  dbUsedBytes: z.number(),
  dbMaxBytes: z.number(),
  dbPct: z.number(),
  status: quotaStatusSchema,
  lastCalcAt: z.string().nullable(),
  dbHardBlockedSince: z.string().nullable(),
  r2HardBlockedSince: z.string().nullable(),
  emergencyUnblockUntil: z.string().nullable(),
});
export type MasterTenantDbQuotaRow = z.infer<typeof masterTenantDbQuotaRowSchema>;

/** `GET /master/storage/db/tenants/:tenantId` → `data` (dettaglio piatto). */
export const masterTenantDbQuotaDetailSchema = masterTenantDbQuotaRowSchema.extend({
  tenantName: z.string().nullable(),
  r2UsedBytes: z.number(),
  r2MaxBytes: z.number(),
  r2Pct: z.number(),
  r2Status: quotaStatusSchema,
  emergencyUnblockReason: z.string().nullable(),
  embeddingMediaUsedBytes: z.number(),
  blockedCategories: z.array(z.string()),
  schemaReady: z.boolean(),
  alerts: z.array(z.object({ pct: z.number(), sentAt: z.string().nullable() })),
});
export type MasterTenantDbQuotaDetail = z.infer<typeof masterTenantDbQuotaDetailSchema>;

// ─── Alert di servizio (campanella master) ──────────────────────────────────

/** `GET /master/alerts` → `data[]` (route `master/alerts.routes.ts`). */
export const masterServiceAlertSchema = z.object({
  id: z.string(),
  tenant_id: z.string().nullable().optional(),
  tenant_name: z.string().nullable().optional(),
  tenant_slug: z.string().nullable().optional(),
  severity: z.enum(['info', 'warning', 'error', 'critical']),
  category: z.string(),
  title: z.string(),
  message: z.string(),
  metadata: z.object({
    server: z.string().optional(),
    host: z.string().optional(),
    service: z.string().optional(),
    eventType: z.string().optional(),
    severity: z.string().optional(),
    suggestion: z.string().optional(),
    timestamp: z.string().optional(),
  }).nullable().optional(),
  is_dismissed: z.boolean(),
  dismissed_at: z.string().nullable().optional(),
  created_at: z.string(),
});
export type MasterServiceAlert = z.infer<typeof masterServiceAlertSchema>;
